/**
 * 商务风尚模版 - VIP会员开通页
 *
 * 设计理念：深墨绿 Hero + 商务对比表 + 套餐卡
 * 与 sunset（金色渐变 Hero + 圆角卡）完全不同的排版骨架：
 * - 顶部：深墨绿 Hero 区（金色皇冠 + 商务标语）
 * - 中段：普通用户 vs VIP 功能对比表（方角表格）
 * - 底部：3栏套餐卡片（推荐卡深色底 + 金色边框）
 */

import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/templates/executive/components/Navbar";
import ExecutiveFooter from "@/templates/executive/components/Footer";
import { useAuth } from "@/hooks/useAuth";
import { getVipPlans } from "@/utils/adminApi";

// ==================== API 层 ====================

interface PaymentConfig {
  alipay_enabled: boolean;
  wechat_enabled: boolean;
  qrcode_enabled: boolean;
  qrcode_priority: boolean;
  alipay_qrcode: string;
  wechat_qrcode: string;
}

async function fetchPaymentConfig(): Promise<PaymentConfig | null> {
  try {
    const token = localStorage.getItem("admin_token") || localStorage.getItem("token");
    const siteId = localStorage.getItem('current_site_id') || '';
    const res = await fetch("/api/admin/settings/payment", {
      method: "GET",
      headers: {
        "Authorization": token ? `Bearer ${token}` : "",
        'X-Site-Id': siteId,
      },
    });
    const data = await res.json();
    if (data.code === 0 && data.data) {
      return {
        alipay_enabled: !!data.data.alipay_enabled,
        wechat_enabled: !!data.data.wechat_enabled,
        qrcode_enabled: !!data.data.qrcode_enabled,
        qrcode_priority: !!data.data.qrcode_priority,
        alipay_qrcode: data.data.alipay_qrcode || "",
        wechat_qrcode: data.data.wechat_qrcode || "",
      };
    }
    return null;
  } catch (e) {
    console.error("读取支付配置失败", e);
    return null;
  }
}

export default function ExecutiveVipPage() {
  const { isLoggedIn } = useAuth();

  const [packages, setPackages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<string | number>(1);
  const [payMethod, setPayMethod] = useState<"wechat" | "alipay" | "qrcode">("wechat");
  const [showPayModal, setShowPayModal] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig | null>(null);

  useEffect(() => {
    Promise.all([
      getVipPlans().then((data) => {
        // 将后台数据映射为前端展示格式
        if (Array.isArray(data) && data.length > 0) {
          const mapped = data.map((p: any) => ({
            id: p.id,
            name: p.name || "VIP套餐",
            price: String(p.amount ?? p.price ?? 0),
            duration: (p.duration_months ?? p.duration ?? 1) * 30,  // 月×30=天
            duration_months: p.duration_months ?? p.duration ?? 1,
            description: p.description || "",
            is_recommended: !!(p.is_recommended ?? false),
            features: Array.isArray(p.benefits) ? p.benefits : (p.features || []),
          }));
          setPackages(mapped);
          // 默认选中第一个套餐
          if (mapped.length > 0) setSelectedPlan(mapped[0].id);
        }
      }).catch(() => {}),
      fetchPaymentConfig().then(setPaymentConfig).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const handleBuy = useCallback((pkg: any) => {
    if (!isLoggedIn) {
      window.location.href = `/login?redirect=/vip`;
      return;
    }
    setSelectedPlan(pkg.id);
    setShowPayModal(true);
  }, [isLoggedIn]);

  // 套餐数据（必须在 currentPkg 之前定义，避免 TDZ 运行时错误）
  const displayPackages = packages.length > 0 ? packages : [
    { id: 1, name: "月度VIP", price: "29.9", duration: 30, description: "适合短期需求", is_recommended: false },
    { id: 2, name: "季度VIP", price: "79.9", duration: 90, description: "性价比之选", is_recommended: true },
    { id: 3, name: "年度VIP", price: "239.9", duration: 365, description: "最高性价比", is_recommended: false },
  ];

  // 功能对比数据
  const compareFeatures = [
    { feature: "发布信息数", free: "5条/月", vip: "无限发布" },
    { feature: "置顶推荐", free: "✕", vip: "✓ 每日刷新" },
    { feature: "加V认证", free: "✕", vip: "✓ 官方认证" },
    { feature: "图片数量", free: "3张", vip: "20张" },
    { feature: "联系方式", free: "基础", vip: "完整展示" },
    { feature: "数据统计", free: "✕", vip: "✓ 详细报表" },
    { feature: "优先客服", free: "✕", vip: "✓ 专属通道" },
    { feature: "纯净体验", free: "✕", vip: "✓ 无广告干扰" },
  ];

  // 支付方式判断
  const showAlipay = !!paymentConfig?.alipay_qrcode && (!!paymentConfig?.qrcode_enabled || !!paymentConfig?.alipay_enabled);
  const showWechat = !!paymentConfig?.wechat_qrcode && (!!paymentConfig?.qrcode_enabled || !!paymentConfig?.wechat_enabled);
  const currentPkg = displayPackages.find((p: any) => p.id === selectedPlan) || displayPackages[0];

  return (
    <div className="min-h-screen bg-[#F5F6F4]">
      <Navbar />

      {/* ── Hero 区：深墨绿商务感 ── */}
      <section className="relative bg-gradient-to-br from-[#1F4E3D] via-[#1A4334] to-[#12382A] py-16 overflow-hidden">
        {/* 装饰 */}
        <div className="absolute inset-0">
          <div className="absolute top-10 right-10 w-72 h-72 rounded-full bg-[#C2A04B]/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-[#C2A04B]/5 blur-3xl" />
          <div className="absolute top-1/2 left-1/4 w-4 h-4 rounded-sm bg-[#DDC583]/20" />
          <div className="absolute top-1/4 right-1/3 w-3 h-3 rounded-sm bg-[#DDC583]/15" />
          <div className="absolute bottom-1/3 right-1/4 w-5 h-5 rounded-sm bg-[#C2A04B]/30" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#C2A04B]/15 border border-[#C2A04B]/40 rounded-sm text-[#DDC583] text-xs font-semibold mb-6">
            <i className="ri-vip-crown-line" />
            VIP 会员专属特权
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4 leading-tight">
            升级<span className="text-[#DDC583]">VIP</span>，尊享商务特权
          </h1>
          <p className="text-white/70 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            置顶推荐、无限发布、加V认证，让您的信息获得更多曝光
          </p>
        </div>
      </section>

      {/* ── 功能对比表 ── */}
      <section className="relative -mt-6 z-10 pb-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-md shadow-xl shadow-[#1F4E3D]/5 border border-[#E2E8E0] overflow-hidden">
            <div className="p-6 sm:p-8 border-b border-[#EDF0EC]">
              <h2 className="text-xl font-black text-[#1F4E3D] text-center mb-1">功能对比</h2>
              <p className="text-sm text-gray-400 text-center">普通用户 vs VIP 会员</p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#1F4E3D]/5">
                    <th className="text-left px-6 py-4 text-sm font-bold text-[#1F4E3D]">功能</th>
                    <th className="text-center px-6 py-4 text-sm font-semibold text-gray-400">
                      <i className="ri-user-line mr-1" />普通用户
                    </th>
                    <th className="text-center px-6 py-4 text-sm font-semibold text-[#8C6E2E]">
                      <i className="ri-vip-crown-line mr-1" />VIP会员
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#EDF0EC]">
                  {compareFeatures.map((row, idx) => (
                    <tr key={idx} className="hover:bg-[#1F4E3D]/5 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-600 font-medium">{row.feature}</td>
                      <td className="px-6 py-4 text-sm text-gray-400 text-center">{row.free}</td>
                      <td className="px-6 py-4 text-sm text-[#8C6E2E] text-center font-medium">{row.vip}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ── 套餐卡片 ── */}
      <section className="pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-black text-[#1F4E3D] mb-2">选择套餐</h2>
            <p className="text-gray-400 text-sm">选择适合您的VIP方案</p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse bg-white rounded-md border border-[#E2E8E0] p-8">
                  <div className="w-1/2 h-6 bg-gray-200/70 rounded mx-auto mb-4" />
                  <div className="w-1/3 h-10 bg-gray-200/70 rounded mx-auto mb-6" />
                  <div className="space-y-3">
                    {[...Array(5)].map((_, j) => (
                      <div key={j} className="w-full h-4 bg-gray-100 rounded" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {displayPackages.map((pkg) => {
                const isRecommended = pkg.is_recommended;
                return (
                  <div
                    key={pkg.id}
                    className={`relative bg-white rounded-md border-2 transition-all duration-300 flex flex-col overflow-hidden ${
                      isRecommended
                        ? "border-[#C2A04B] shadow-2xl shadow-[#C2A04B]/15 scale-[1.03] z-10"
                        : "border-[#E2E8E0] shadow-lg hover:border-[#C2A04B] hover:shadow-xl"
                    }`}
                  >
                    {isRecommended && (
                      <div className="absolute -top-4 inset-x-0 flex justify-center">
                        <span className="px-5 py-1 bg-gradient-to-r from-[#C2A04B] to-[#A8863A] text-[#1F4E3D] text-xs font-bold rounded-sm shadow-lg shadow-[#C2A04B]/30 flex items-center gap-1">
                          <i className="ri-star-line text-[10px]" />热门推荐
                        </span>
                      </div>
                    )}

                    {/* 卡片头部：深色 */}
                    <div className={`px-6 py-4 ${isRecommended ? "bg-gradient-to-r from-[#1F4E3D] to-[#12382A]" : "bg-[#1F4E3D]/5"}`}>
                      <div className="text-center">
                        <h3 className={`text-lg font-bold mb-1 ${isRecommended ? "text-white" : "text-[#1F4E3D]"}`}>{pkg.name}</h3>
                        <p className={`text-xs ${isRecommended ? "text-white/60" : "text-gray-400"}`}>{pkg.description}</p>
                      </div>
                    </div>

                    <div className="p-8 flex-1 flex flex-col">
                      <div className="text-center mb-6">
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-sm text-gray-400">¥</span>
                          <span className={`text-4xl font-black ${isRecommended ? 'text-[#8C6E2E]' : 'text-[#1F4E3D]'}`}>
                            {pkg.price}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          {pkg.duration >= 365 ? `仅 ¥${(Number(pkg.price) / 12).toFixed(1)}/月` : `共 ${pkg.duration} 天`}
                        </p>
                      </div>

                      <ul className="space-y-3 mb-8 flex-1">
                        {[
                          "无限发布信息",
                          "每日置顶推荐",
                          "加V官方认证",
                          "20张高清图片",
                          "联系方式完整展示",
                        ].map((feature, fi) => (
                          <li key={fi} className="flex items-start gap-2.5">
                            <i className={`ri-check-line text-sm mt-0.5 ${isRecommended ? 'text-[#C2A04B]' : 'text-[#1F4E3D]'}`} />
                            <span className="text-sm text-gray-600">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      <button
                        onClick={() => handleBuy(pkg)}
                        className={`w-full py-3.5 rounded-sm font-bold text-sm transition-all ${
                          isRecommended
                            ? "bg-gradient-to-r from-[#C2A04B] to-[#A8863A] text-[#1F4E3D] shadow-lg shadow-[#C2A04B]/25 hover:from-[#D8B86A] hover:to-[#B8912F]"
                            : "border-2 border-[#C2A04B]/50 text-[#8C6E2E] hover:bg-[#1F4E3D] hover:text-[#DDC583] hover:border-[#1F4E3D]"
                        }`}
                      >
                        立即开通
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* 底部说明 */}
          <div className="mt-10 text-center">
            <p className="text-xs text-gray-400">
              开通即表示同意{" "}
              <Link to="/page/register-agreement" className="text-[#8C6E2E] hover:text-[#C2A04B]">服务协议</Link>
              {" "}和{" "}
              <Link to="/page/privacy" className="text-[#8C6E2E] hover:text-[#C2A04B]">隐私政策</Link>
            </p>
          </div>
        </div>
      </section>

      <ExecutiveFooter />

      {/* ── 支付弹窗 ── */}
      {showPayModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-md p-6 w-full max-w-sm shadow-2xl border border-[#E2E8E0]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-[#1F4E3D] text-lg">确认支付</h3>
              <button
                onClick={() => setShowPayModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-gray-100 transition-colors"
              >
                <i className="ri-close-line text-gray-500 text-lg" />
              </button>
            </div>

            {/* 套餐信息 */}
            <div className="flex items-center gap-3 mb-5 p-3 bg-[#1F4E3D]/5 rounded-sm border border-[#1F4E3D]/10">
              <div className="w-10 h-10 rounded-sm bg-[#1F4E3D] flex items-center justify-center flex-shrink-0">
                <i className="ri-vip-crown-fill text-[#DDC583] text-xl" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800">{currentPkg?.name || "VIP套餐"}</p>
                <p className="text-xs text-gray-500">{currentPkg?.duration || 30}天</p>
              </div>
              <p className="text-xl font-bold text-[#8C6E2E] flex-shrink-0">
                ¥{currentPkg?.price || "29.9"}
              </p>
            </div>

            {/* 支付方式 */}
            {(showWechat || showAlipay) && (
              <>
                <p className="text-sm font-semibold text-gray-700 mb-3">
                  <i className="ri-arrow-down-circle-line text-[#C2A04B] mr-1" />
                  请选择支付方式
                </p>
                <div className="flex gap-3 mb-5">
                  {showWechat && (
                    <button
                      onClick={() => setPayMethod("wechat")}
                      className={`flex-1 flex flex-col items-center gap-2 py-4 rounded-sm text-sm font-medium transition-all cursor-pointer border-2 ${
                        payMethod === "wechat"
                          ? "bg-green-50 text-green-700 border-green-400 shadow-md"
                          : "bg-gray-50 text-gray-500 border-transparent hover:border-gray-300 hover:bg-gray-100"
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl ${
                        payMethod === "wechat" ? "bg-green-100" : "bg-gray-200"
                      }`}>
                        <i className="ri-wechat-pay-fill" />
                      </div>
                      <span>微信支付</span>
                    </button>
                  )}
                  {showAlipay && (
                    <button
                      onClick={() => setPayMethod("alipay")}
                      className={`flex-1 flex flex-col items-center gap-2 py-4 rounded-sm text-sm font-medium transition-all cursor-pointer border-2 ${
                        payMethod === "alipay"
                          ? "bg-blue-50 text-blue-700 border-blue-400 shadow-md"
                          : "bg-gray-50 text-gray-500 border-transparent hover:border-gray-300 hover:bg-gray-100"
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl ${
                        payMethod === "alipay" ? "bg-blue-100" : "bg-gray-200"
                      }`}>
                        <i className="ri-alipay-fill" />
                      </div>
                      <span>支付宝</span>
                    </button>
                  )}
                </div>
              </>
            )}

            {/* 收款码展示区 */}
            <div className="text-center">
              {payMethod === "alipay" && showAlipay && (
                <div>
                  <p className="text-xs text-gray-500 mb-3">请使用支付宝扫码付款</p>
                  <img
                    src={paymentConfig?.alipay_qrcode}
                    alt="支付宝收款码"
                    className="w-48 h-48 mx-auto rounded-sm border border-[#E2E8E0]"
                  />
                </div>
              )}

              {payMethod === "wechat" && showWechat && (
                <div>
                  <p className="text-xs text-gray-500 mb-3">请使用微信扫码付款</p>
                  <img
                    src={paymentConfig?.wechat_qrcode}
                    alt="微信收款码"
                    className="w-48 h-48 mx-auto rounded-sm border border-[#E2E8E0]"
                  />
                </div>
              )}

              {/* 无收款码占位 */}
              {!showAlipay && !showWechat && (
                <>
                  <div className="w-44 h-44 mx-auto mb-4 bg-gray-100 rounded-sm flex items-center justify-center">
                    <i className="ri-qr-code-line text-5xl text-gray-300" />
                  </div>
                  <p className="text-sm text-gray-500 mb-1">支付功能暂未启用</p>
                  <p className="text-xs text-gray-400">请联系管理员配置收款码</p>
                </>
              )}
            </div>

            <div className="mt-5 p-3.5 bg-[#1F4E3D]/5 rounded-sm text-xs text-[#1F4E3D] text-center font-medium border border-[#1F4E3D]/10">
              <i className="ri-information-line mr-1" />
              支付成功后将自动开通VIP权益
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
